/**
 * NMAC / SWC / Eval ring layer.
 * Generated via turf.js circle().
 */
import { circle as turfCircle } from '@turf/turf';
import type { Feature, Polygon } from 'geojson';
import type { GeoJSONSource, Map } from 'mapbox-gl';
import { droneFilterKey, visibleFleet } from '@/lib/map/droneFilter';
import { useDronesStore } from '@/lib/store/drones';
import { useUiStore } from '@/lib/store/ui';

const SRC = 'src-rings';
const LAYER = 'lyr-rings';

export function ensureRingsLayer(map: Map) {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    map.addLayer({
      id: LAYER,
      type: 'line',
      source: SRC,
      paint: {
        'line-color': [
          'match',
          ['get', 'ring'],
          'nmac', '#c41a1a',
          'swc', '#d97706',
          'eval', '#6b7280',
          /* default */ '#6b7280',
        ],
        'line-width': 1.5,
        'line-dasharray': [
          'match',
          ['get', 'ring'],
          'nmac', ['literal', [1]],
          'swc', ['literal', [4, 2]],
          'eval', ['literal', [2, 3]],
          ['literal', [1]],
        ],
        'line-opacity': 0.8,
      },
    });
  }
}

export function syncRingsLayer(map: Map) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const ui = useUiStore.getState();
  const sel = useDronesStore.getState().selectedId;
  const fleet = visibleFleet();
  const drones = sel ? fleet.filter((d) => d.id === sel) : fleet;

  const features: Feature<Polygon>[] = [];
  for (const d of drones) {
    const t = d.telemetry;
    const center: [number, number] = [t.lng, t.lat];
    if (ui.showNMAC) {
      const c = turfCircle(center, t.nmacRadius, { units: 'meters', steps: 64 });
      c.properties = { ring: 'nmac', drone_id: t.drone_id };
      features.push(c as Feature<Polygon>);
    }
    if (ui.showSWC) {
      const c = turfCircle(center, t.swcRadius, { units: 'meters', steps: 64 });
      c.properties = { ring: 'swc', drone_id: t.drone_id };
      features.push(c as Feature<Polygon>);
    }
    if (ui.showEval) {
      const c = turfCircle(center, t.evalRadius, { units: 'meters', steps: 64 });
      c.properties = { ring: 'eval', drone_id: t.drone_id };
      features.push(c as Feature<Polygon>);
    }
  }

  src.setData({ type: 'FeatureCollection', features });
}

export function subscribeRingsLayer(map: Map): () => void {
  ensureRingsLayer(map);
  syncRingsLayer(map);
  let rafId: number | null = null;
  const trigger = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncRingsLayer(map);
    });
  };
  const unsub1 = useDronesStore.subscribe((s) => s.byId, trigger);
  const unsub2 = useDronesStore.subscribe((s) => s.selectedId, trigger);
  const unsub3 = useUiStore.subscribe(
    (s) => `${s.showNMAC}-${s.showSWC}-${s.showEval}-${droneFilterKey(s)}`,
    trigger,
  );
  return () => {
    unsub1();
    unsub2();
    unsub3();
    if (rafId !== null) cancelAnimationFrame(rafId);
  };
}
